import { jsx } from "react/jsx-runtime";
import PropTypes from "prop-types";
import { useRef } from "react";
import { useCoreVideoSdk } from "./useCoreVideoSdk.js";
import { useCvsdkSession } from "./useCvsdkSession.js";
import { log } from "../../log.js";
import { useReportAnalyticsMetadata } from "../../lib/hooks/useReportAnalyticsMetadata.js";
import { getConvivaPlayerName } from "./configs.js";
import { usePlayerController } from "./usePlayerController.js";
import { useGenerateSessionConfig } from "./generateSessionConfig/useGenerateSessionConfig.js";
import { getPreset } from "../../demoAppConfigPresets.js";
import { TestPresetTag } from "../../testStreamPreset.js";
const appVersion = "6.12.0";
const cvsdkVersion = "11.0.1";
log({ appVersion, cvsdkVersion });
function CoreVideoPlayer({
  mParticleId = "",
  mvpdHash = "",
  mvpdId,
  usPrivacy,
  gpp,
  gdpr,
  metadata,
  signRequest,
  onVideoControllerReady,
  ...props
}) {
  console.log("### YALC TEST: Omega Player component successfully loaded!");
  const { initializationOptions, streamConfig } = transitionalPresetLookup(props);
  const coreVideoContainer = useRef(null);
  const autoplayRef = useRef(!!props.autoplay);
  const mutedAutoplayRef = useRef(!!props.mutedAutoplay);
  const instanceId = useRef(crypto.randomUUID()).current;
  useReportAnalyticsMetadata(metadata, instanceId);
  const { cvsdk, sessionKey } = useCoreVideoSdk(
    {
      appVersion,
      appName: initializationOptions.appName,
      appBrand: initializationOptions.appBrand,
      deviceAdvertisingId: "",
      appIdentifier: getConvivaPlayerName(initializationOptions.appBrand),
      mtEndpoint: initializationOptions.mtEndpoint,
      vamEndpoint: initializationOptions.vamEndpoint,
      convivaEnvironment: initializationOptions.convivaEnvironment
    },
    signRequest,
    streamConfig
  );
  const latestSessionConfig = useGenerateSessionConfig({
    sessionKey,
    streamConfig,
    mParticleId,
    usPrivacy,
    gpp,
    gdpr,
    mvpdHash,
    autoplay: autoplayRef.current,
    mutedAutoplay: mutedAutoplayRef.current
  });
  const videoHTMLContainer = coreVideoContainer.current ?? void 0;
  const playerController = usePlayerController({ cvsdk, videoHTMLContainer, mvpdId, instanceId });
  useCvsdkSession({ latestSessionConfig, onVideoControllerReady, playerController });
  return /* @__PURE__ */jsx("div", { ref: coreVideoContainer, className: "omega_coreVideoContainer" });
}
CoreVideoPlayer.propTypes = {
  initializationOptions: PropTypes.shape({
    appName: PropTypes.string.isRequired,
    appBrand: PropTypes.string.isRequired,
    vamEndpoint: PropTypes.oneOf(["dev", "prod"]),
    mtEndpoint: PropTypes.oneOf(["dev", "prod"]),
    convivaEnvironment: PropTypes.oneOf(["dev", "prod"])
  }).isRequired,
  /**
   * @param {any} props
   */
  streamConfig({
    streamId,
    streamConfig,
    vodMetadata,
    streamData,
    initializationOptions,
    vamEndpoint,
    mtEndpoint,
    convivaEnvironment
  }) {
    if (!streamConfig) {
      return new Error("streamConfig prop not provided");
    }
    if (!initializationOptions) {
      return new Error("initializationOptions prop not provided");
    }
    if (streamId) {
      const preset = getPreset(streamId, vodMetadata, streamData);
      if (preset) {
        try {
          checkIsSubsetOf(streamConfig, preset.streamConfig);
        } catch (e) {
          console.info(streamConfig, preset);
          return new Error(`Could not validate streamConfig matches a known stream. ${e.message}`);
        }
        const {
          vamEndpoint: _vamEndpoint,
          mtEndpoint: _mtEndpoint,
          convivaEnvironment: _convivaEnvironment,
          ...required
        } = preset.initializationOptions;
        try {
          checkIsSubsetOf(initializationOptions, required);
        } catch (e) {
          console.info(initializationOptions, required);
          return new Error(`Could not validate streamConfig matches a known stream. ${e.message}`);
        }
        if (vamEndpoint !== initializationOptions.vamEndpoint) {
          return new Error(
            `vamEndpoint does not match preset for streamId: ${streamId}: ${vamEndpoint} !== ${initializationOptions.vamEndpoint}`
          );
        }
        if (mtEndpoint !== initializationOptions.mtEndpoint) {
          return new Error(
            `mtEndpoint does not match preset for streamId: ${streamId}: ${mtEndpoint} !== ${initializationOptions.mtEndpoint}`
          );
        }
        if (convivaEnvironment !== initializationOptions.convivaEnvironment) {
          return new Error(
            `convivaEnvironment does not match preset for streamId: ${streamId}: ${convivaEnvironment} !== ${initializationOptions.convivaEnvironment}`
          );
        }
      }
    }
    return void 0;
  }
};
function checkIsSubsetOf(a, b, property = "") {
  function err() {
    return new Error(`Properties do not match: ${property}: ${a} !== ${b}`);
  }
  if (a === b) {
    return true;
  }
  if (typeof a === "object") {
    if (a === null || b === null) {
      throw err();
    }
    if (typeof b === "object") {
      Object.keys(b).forEach((key) => checkIsSubsetOf(a[key], b[key], `${property}.${key}`));
      return true;
    }
  }
  throw err();
}
function transitionalPresetLookup(props) {
  const preset = getPreset(props.streamId || "", props.vodMetadata, props.streamData);
  const initializationOptions = props.initializationOptions || (preset == null ? void 0 : preset.initializationOptions) || {
    appName: "",
    appBrand: ""
  };
  if (props.mtEndpoint) {
    initializationOptions.mtEndpoint = props.mtEndpoint;
  }
  if (props.vamEndpoint) {
    initializationOptions.vamEndpoint = props.vamEndpoint;
  }
  if (props.convivaEnvironment && props.convivaEnvironment !== "none") {
    initializationOptions.convivaEnvironment = props.convivaEnvironment;
  }
  const streamConfig = (preset == null ? void 0 : preset.streamConfig) || props.streamConfig;
  if (!initializationOptions || !streamConfig) {
    throw new Error("No initializationOptions or streamConfig found");
  }
  return TestPresetTag in streamConfig ? { [TestPresetTag]: true, initializationOptions, streamConfig } : { initializationOptions, streamConfig };
}
export {
  CoreVideoPlayer,
  CoreVideoPlayer as default };
//# sourceMappingURL=dist/components/CoreVideoPlayer/index.js.map